import * as _ from 'lodash';
import { BigNumber } from 'bignumber.js';
import { Payment } from '../../objects/Payment';
import { TimeString } from '../../misc/TimeString';
import { TromanError } from '../../../common/TromanError';
import { wageModelInfos } from '../../objects/WageType';
import { MeetingAccountSource } from '../../objects/Meeting';
import { CalculatedPayment } from './CalculatedPayment';

/**
 * @typedef { import('../../objects/Attendance').Attendance } Attendance
 * @typedef { import('../../objects/WageCalculation').WageCalculation } WageCalculation
 * @typedef { import('../../objects/WageType').WageType } WageType
 * @typedef { import('../../objects/WageType').VariablePrice } VariablePrice
*/

/**
 * 
 * @param {Number|string} unitPrice
 * @param {Number|string} number
 */
export function calculateAmount(unitPrice, number) {
    var up = new BigNumber(unitPrice);
    var n = new BigNumber(number);
    return up.times(n).dp(4).toNumber();
}

/**
 * 
 * @param {Number} amount
 */
export function roundAmount(value) {
    return new BigNumber(value).dp(4).toNumber();
}

/**
 * 
 * @param {Number} unitPrice
 */
export function roundUnitPrice(value) {
    return new BigNumber(value).dp(4).toNumber();
}

/**
 * 
 * @param {Number} value
 */
export function roundNumber(value) {
    return new BigNumber(value).dp(8).toNumber();
}

export class ActiveWageType {
    /**
     * 
     * @param {WageType} wageType
     * @param {any} period
     */
    constructor(wageType, period) {
        this.wageType = wageType;
        this.period = period;
        this.wageModel = wageModelInfos[period.WageModel];
    }

    /**
     * 
     * @param {WageType} wageType
     * @param {Date} date
     */
    static forDate(wageType, date) {
        if (wageType == null) return null;

        const period = wageType.getPeriod(date);

        if (!period) {
            throw new TromanError('missing_period', `Löneartsuppgifter saknas för mötesdatumet (${wageType.Code} ${wageType.Name}).`);
        }

        return new ActiveWageType(wageType, period);
    }
}

export class PaymentsCalculation {

    /**
     *
     * @param {Meeting} attendance
     */
    constructor(attendance) {
        this.attendance = attendance;

        this.accountSource = attendance.Meeting.AccountSource;
        this.meetingOrganizationAccount = attendance.Meeting.Organization.Account;
        this.meetingAccount = attendance.Meeting.Account;

        /** @type {CalculatedPayment[]} */
        this.result = [];
    }

    /**
     * 
     * @param {String} timeString
     */
    getDuration(timeString) {
        var ts = TimeString.parse(timeString);
        if (ts == null) throw new TromanError('invalid_timestring', 'Felaktig tidsangivelse.');
        if (!ts.isClosed) throw new TromanError('open_timestring', 'Tidsangivelse saknar sluttid.');
        if (ts.duration < 0) throw new TromanError('invalid_duration');
        return ts.duration;
    }

    /**
     * 
     * @param {ActiveWageType} awt
     */
    createBasicPayment(awt) {
        let payment = new Payment();
        
        payment.WageTypeId = awt.wageType.Id;
        payment.WageType = awt.wageType;

        if (awt.period.UseMeetingAccountSettings) {
            if (this.accountSource === MeetingAccountSource.meetingOrganization) {
                payment.Account = this.meetingOrganizationAccount;
            } else if (this.accountSource === MeetingAccountSource.meetingAccount) {
                payment.Account = this.meetingAccount;
            } else {
                payment.Account = this.attendance.Mission.MandatePeriod.Organization.Account;
            }
        } else {
            payment.Account = awt.period.Account;
        }

        return new CalculatedPayment(payment);
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} minutes
     */
    calculateNumberForMinutes(awt, minutes) {
        const wtp = awt.period;
        const subperiods = Math.ceil(minutes / wtp.SubPeriod);
        const periods = subperiods * wtp.SubPeriod / wtp.UnitPeriod;
        return periods;
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} minutes
     */
    addTimePayment(awt, minutes) {
        if (awt.wageModel.id !== 0) throw new TromanError('invalid_wage_model');
        if (!awt.wageModel.hasUnitPrice) throw new TromanError('invalid_wage_model');

        const periods = this.calculateNumberForMinutes(awt, minutes);
        return this.addNumberPayment(awt, periods);
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} minutes
     */
    addVariablePayment(awt, minutes) {
        if (awt.wageModel.id !== 1) throw new TromanError('invalid_wage_model');

        /** @type {VariablePrice} */
        const vp = awt.period.VariablePrice;
        const amount = vp.findAmount(minutes);
        if (amount == null) throw new TromanError('missing_variable', 'Variabelt pris saknas för ' + minutes + ' minuter.');

        const periods = this.calculateNumberForMinutes(awt, minutes);

        let cp = this.createBasicPayment(awt);
        cp.payment.Number = roundNumber(periods);
        cp.payment.Amount = roundAmount(amount);
        cp.payment.UnitPrice = null;

        this.result.push(cp);
        return cp;
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} number
     */
    addNumberPayment(awt, number) {
        if (awt.wageModel.hasUnitPrice && _.isNumber(awt.period.UnitPrice)) {
            return this.addNumberUnitPayment(awt, number, awt.period.UnitPrice);
        } else {
            return this.addNumberOnlyPayment(awt, number);
        }
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} number
     */
    addNumberOnlyPayment(awt, number) {
        if (typeof number !== 'number') throw new TromanError('invalid_number');
        if (number <= 0) throw new TromanError('invalid_argument');

        let cp = this.createBasicPayment(awt);
        cp.payment.Number = roundNumber(number);
        this.result.push(cp);
        return cp;
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} number
     * @param {number} unitPrice
     */
    addNumberUnitPayment(awt, number, unitPrice) {
        if (typeof number !== 'number') throw new TromanError('invalid_number');
        if (typeof unitPrice !== 'number') throw new TromanError('invalid_unitprice');
        if (number <= 0) throw new TromanError('invalid_argument');

        let cp = this.createBasicPayment(awt);
        cp.payment.Number = roundNumber(number);
        cp.payment.UnitPrice = roundUnitPrice(unitPrice);
        cp.payment.Amount = calculateAmount(unitPrice, number);
        this.result.push(cp);
        return cp;
    }

    /**
     * @param {ActiveWageType} awt
     * @param {number} amount
     */
    addAmountPayment(awt, amount) {
        if (typeof amount !== 'number') throw new TromanError('invalid_number');

        let cp = this.createBasicPayment(awt);
        cp.payment.Amount = roundAmount(amount);

        this.result.push(cp);
        return cp;
    }
}
